<template lang="pug">
v-app.pa-0.ma-0(:class="appClass")
  j-header-menu.center(
    v-if="isLoggedIn"
    :back="enableBack"
    :is-mobile="isMobile"
    :class="isMobile ? 'main--mobile' : ''"
    @open-menu="isOpenNav = true"
    :is-resize="isResize",
    :isNotFixedHeader="hasReturnToTop && isMobile"
  )
  v-main
    j-navigation-menu(
      v-if="isLoggedIn"
      v-model="isOpenNav"
      @toggle="toggleNavigation",
      :is-mobile="isMobile"
    )
    JSplash(v-if="computedLoading")
    router-view.router-view(:class="isMobile? 'main--mobile' : ''" :key="$route.path")
  v-btn.scroll-top-btn(v-if="showScrollButton" @click="scrollToTop" class="mx-2" fab dark large color="white" outlined)
    v-icon(class="custom-icon" color="primary") mdi-arrow-up-bold
  version-notification()
  //- loading page indicator
  app-loading
  app-confirm
</template>

<script>
import {
  defineComponent,
  ref,
  watch,
  getCurrentInstance,
  computed,
  onMounted,
  onUnmounted,
  onBeforeUnmount
} from 'vue'
import {
  JHeaderMenu,
  JNavigationMenu,
  JSplash,
  VersionNotification,
  AppLoading,
  AppConfirm
} from 'components'
import { urlPath, checkScreenSize } from 'utils'
import { NON_LOGGED_IN_PAGES } from 'utils/constants'

const App = defineComponent({
  components: {
    JHeaderMenu,
    JNavigationMenu,
    JSplash,
    VersionNotification,
    AppLoading,
    AppConfirm
  },
  setup() {
    const instance = getCurrentInstance()
    const { $root, $store } = instance.proxy
    /**
     * get nonLoggedInPage to hidden header and navigation for pages that they have not logged in.
     *  - case 1: if we first go to page or reload page then $route.name = null
     *  - case 2: we go to non logged in pages
     * @type {ComputedRef<unknown>}
     */
    const isLoggedIn = computed(() => {
      const noAuth =
        $root.$route.name == null ||
        NON_LOGGED_IN_PAGES.some((page) => page.name === $root.$route.name)
      return !noAuth
    })

    const enableBack = ref(false)
    const isOpenNav = ref(false)
    const start = ref(true)
    const screenType = ref(checkScreenSize())
    const isResize = ref(false)
    const isMobile = computed(() => {
      const { path } = $root.$route
      if (path.includes('mobile') || path.includes('tablet')) return true
      switch (screenType.value) {
        case 'mobile':
          return true
        case 'tablet':
          return true
        case 'pc':
          return false
        default:
          return false
      }
    })
    const toggleNavigation = () => {
      isOpenNav.value = !isOpenNav.value
    }

    const computedLoading = computed(() => {
      return $store.state.loadingIndicator
    })
    const appClass = computed(() => {
      const { path } = $root.$route
      if (path.includes('mobile')) return 'main-app'
      switch (screenType.value) {
        case 'mobile':
          return 'main-app'
        case 'pc':
          return 'v-main__wrap'
        default:
          return 'v-main__wrap'
      }
    })
    const showScrollButton = ref(false)

    const scrollToTop = () => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      })
    }
    const hasReturnToTop = ref(false)
    const checkScrollPosition = () => {
      showScrollButton.value =
        window.pageYOffset > 100 && hasReturnToTop.value && isMobile.value
    }

    watch(
      () => $root.$route,
      () => {
        isResize.value = screen.width <= 375
        const { path } = $root.$route
        start.value = path !== '/'
        const isMainUrl = urlPath.DISABLE_NAVIGATION.find((url) => url === path)
        enableBack.value = !isMainUrl

        // check return to top button
        const ASSIGNMENT_MOBILE_FORM_REGEX = /^\/assignment\/mobile\/form\/\d+$/
        hasReturnToTop.value = !urlPath.NOT_HAS_RETURN_TO_TOP.find(
          (url) => url === path || ASSIGNMENT_MOBILE_FORM_REGEX.test(path)
        )

        // check histories
        // if (history.state.histories.length > 1) {
        //   enableBack.value = !isMainUrl
        // } else {
        //   enableBack.value = false
        // }
        if (path.includes('mobile')) {
          enableBack.value = false
        }
      }
    )
    const onWidthChange = () => {
      screenType.value = checkScreenSize()
    }
    onMounted(() => {
      window.addEventListener('resize', onWidthChange)
      window.addEventListener('scroll', checkScrollPosition)
    })
    onBeforeUnmount(() => {
      window.removeEventListener('scroll', checkScrollPosition)
    })
    onUnmounted(() => window.removeEventListener('resize', onWidthChange))
    return {
      isLoggedIn,
      isOpenNav,
      toggleNavigation,
      enableBack,
      checkScreenSize,
      isMobile,
      computedLoading,
      appClass,
      isResize,
      showScrollButton,
      scrollToTop,
      hasReturnToTop
    }
  }
})
export default App
</script>

<style lang="sass">
@import '@/style/css/master.sass'
//This css to prevent double scroll on mobile, tablet
@media only screen and (min-device-width: 200px) and (max-device-width: 1200px)
.main-app > .v-application--wrap
  max-height: -webkit-fill-available
  min-height: -webkit-fill-available
.router-view
  padding-left: 8px
  padding-right: 8px
@media (max-width: 820px)
  .nav-hide
    padding-left: 2px
.v-main__wrap
  position: inherit !important
.center
  margin: auto
.main--mobile
  background-color: white
  height: 100vh
.scroll-top-btn
  position: fixed
  top: 40px
  right: 16px
  z-index: 999
  padding: 10px
  border: 3px solid var(--v-primary-base) !important
  opacity: 0.6
  border-radius: 50px
  cursor: pointer
  @media (max-width: 820px)
    top: unset
    bottom: 42px
    right: 8px
    padding: 4px
    height: 40px !important
    width: 40px !important
.custom-icon
  font-size: 36px !important
  @media (max-width: 820px)
    font-size: 30px !important
.v-list-item, .v-chip__content, .v-label, .link-text, input
  white-space: pre

.custom-warning-toast
  color: black !important
  button
    color: black !important
  .Vue-Toastification__toast-body
    color: black !important
    font-size: 15px !important

.v-list-item__mask
  all: revert !important
</style>
